import { useMemo, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { HelpMessage, HelpTicket, HelpTicketStatus } from "@backstory/protocol";
import { EmptyListMark, EmptyState, ErrorState, Skeleton } from "../../components/States";
import { AutoLoadMore, BusyButton, Field, PageHeader, SearchSelect, useToast } from "../../components/ui";
import { UnsavedGuard } from "../../components/UnsavedChanges";
import { formatDateTime, formatRelative } from "../../lib/format";
import { useAuth } from "../../auth/store";
import { helpApi, helpKeys } from "../staff/api";
import { useDebounced } from "../staff/customers";
import { HelpMetrics, KindBadge, PAGE_SIZE, ThreadMessage, TicketSkeletonRows, TicketTitle, ticketMetrics, ticketTone } from "./shared";
import { ImagePicker, MessageImages, pastedImages, plainTextIssue, takeImages } from "./images";
import { ReportDialog } from "./ReportDialog";
import "./help.css";

const statusLabel: Record<HelpTicketStatus, string> = {
  open: "Open",
  waiting_on_us: "Waiting on Backstory",
  waiting_on_them: "Waiting on you",
  planned: "Planned",
  declined: "Not planned",
  resolved: "Resolved",
};
const statusOptions = [
  { value: "all", label: "All statuses" },
  { value: "open", label: "Open" },
  { value: "waiting_on_us", label: "Waiting on Backstory" },
  { value: "waiting_on_them", label: "Waiting on you" },
  { value: "planned", label: "Planned" },
  { value: "declined", label: "Not planned" },
  { value: "resolved", label: "Resolved" },
];

function BackToHelp() {
  return (
    <Link to="/help" className="btn ghost help-back" aria-label="Back to help">
      ←
    </Link>
  );
}

export function HelpListPage() {
  const auth = useAuth();
  const orgId = auth.currentOrgId ?? "";
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState("all");
  const [qInput, setQInput] = useState("");
  const q = useDebounced(qInput.trim(), 250);
  const filters = useMemo(() => ({ status, q: q || undefined }), [status, q]);
  const filtered = !!q || status !== "all";
  const list = useInfiniteQuery({
    queryKey: helpKeys.listFiltered(orgId, filters),
    queryFn: ({ pageParam }) => helpApi.list(orgId, { ...filters, limit: PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((n, p) => n + p.items.length, 0);
      return last.items.length === PAGE_SIZE && loaded < last.total ? loaded : undefined;
    },
    enabled: !!orgId,
    placeholderData: keepPreviousData,
  });
  const rows = list.data?.pages.flatMap((p) => p.items) ?? [];
  const stats = useQuery({ queryKey: helpKeys.stats(orgId), queryFn: () => helpApi.stats(orgId), enabled: !!orgId });
  const metrics = useMemo(() => ticketMetrics(stats.data, "customer"), [stats.data]);
  const clear = () => {
    setQInput("");
    setStatus("all");
  };
  return (
    <div className="page help-page">
      <PageHeader
        title="Help"
        subtitle="Your requests, where each one stands, and the replies you have been sent."
        actions={<button type="button" className="btn primary" onClick={() => setOpen(true)}>Report this page</button>}
      >
        <div className="toolbar help-filters help-kb-filters" aria-label="Help filters">
          <input
            className="input help-search"
            type="search"
            placeholder="Search your requests"
            aria-label="Search your requests"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            autoComplete="off"
          />
          <SearchSelect value={status} options={statusOptions} onChange={setStatus} aria-label="Status" searchPlaceholder="Search statuses…" />
        </div>
      </PageHeader>
      {!stats.isError && <HelpMetrics label="Your requests at a glance" metrics={metrics} loading={stats.isPending} activeStatus={status} onStatus={setStatus} />}
      {list.isError ? (
        <ErrorState error={list.error} retry={() => void list.refetch()} />
      ) : !list.isPending && rows.length === 0 ? (
        <div className="card">
          {filtered ? (
            <EmptyState
              mark={<EmptyListMark />}
              title="No requests match"
              hint="Try a different search or status."
              action={<button type="button" className="btn ghost" onClick={clear}>Clear filters</button>}
            />
          ) : (
            <EmptyState mark={<EmptyListMark />} title="No requests yet" hint="Report a page when something is wrong or you need help." />
          )}
        </div>
      ) : (
        <>
          <div className="card stack-table help-queue help-queue-customer">
            <table className="table" aria-label="Help requests" aria-busy={list.isPending || list.isFetchingNextPage || undefined}>
              <thead>
                <tr>
                  <th className="help-col-request">Request</th>
                  <th className="help-col-status">Status</th>
                  <th className="help-col-updated">Updated</th>
                </tr>
              </thead>
              <tbody>
                {list.isPending ? <TicketSkeletonRows count={4} staff={false} /> : rows.map((t) => <CustomerRow key={t.id} ticket={t} />)}
                {list.isFetchingNextPage && <TicketSkeletonRows count={3} staff={false} />}
              </tbody>
            </table>
          </div>
          <AutoLoadMore hasMore={!!list.hasNextPage} loading={list.isFetchingNextPage} loadMore={() => void list.fetchNextPage()} label="Loading more requests" />
        </>
      )}
      {open && orgId && <ReportDialog orgId={orgId} onClose={() => setOpen(false)} />}
    </div>
  );
}

function CustomerRow({ ticket: t }: { ticket: HelpTicket }) {
  const navigate = useNavigate();
  const open = () => void navigate({ to: "/help/$id", params: { id: t.id } });
  return (
    <tr className="clickable" tabIndex={0} onClick={open} onKeyDown={(e) => e.key === "Enter" && open()}>
      <td className="help-col-request" data-label="Request">
        <TicketTitle kind={t.kind} title={t.title} />
      </td>
      <td className="help-col-status" data-label="Status">
        <span className={`badge ${ticketTone(t.status)}`}>{statusLabel[t.status]}</span>
      </td>
      <td className="help-col-updated muted" data-label="Updated" title={formatDateTime(t.updatedAt)}>
        {formatRelative(t.updatedAt)}
      </td>
    </tr>
  );
}

export function HelpTicketPage({ id }: { id: string }) {
  const auth = useAuth();
  const orgId = auth.currentOrgId ?? "";
  const toast = useToast();
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const [images, setImages] = useState<File[]>([]);
  const [imageError, setImageError] = useState<string>();
  const textIssue = plainTextIssue(text);
  const ticket = useQuery({
    queryKey: helpKeys.ticket(orgId, id),
    queryFn: () => helpApi.get(orgId, id),
    enabled: !!orgId,
  });
  const reply = useMutation({
    mutationFn: () => helpApi.reply(orgId, id, text.trim(), images),
    onSuccess: (detail) => {
      qc.setQueryData(helpKeys.ticket(orgId, id), detail);
      void qc.invalidateQueries({ queryKey: helpKeys.list(orgId) });
      setText("");
      setImages([]);
      setImageError(undefined);
      toast.push("success", "Reply sent");
    },
    onError: (e: Error) => toast.push("error", e.message),
  });
  const feedback = useMutation({
    mutationFn: (helpful: boolean) => helpApi.feedback(orgId, id, helpful),
    onSuccess: (detail, helpful) => {
      qc.setQueryData(helpKeys.ticket(orgId, id), detail);
      toast.push("success", helpful ? "Thanks for letting us know" : "We will take another look");
    },
    onError: (e: Error) => toast.push("error", e.message),
  });
  if (!orgId) return null;
  if (ticket.isError) {
    return (
      <div className="page help-page">
        <PageHeader back={<BackToHelp />} title="Request" />
        <ErrorState error={ticket.error} retry={() => void ticket.refetch()} />
      </div>
    );
  }
  const detail = ticket.data;
  if (!detail) {
    return (
      <div className="page help-page">
        <PageHeader back={<BackToHelp />} title="Loading request…" />
        <Skeleton lines={6} />
      </div>
    );
  }
  const t = detail.ticket;
  const lastMessage = detail.messages[detail.messages.length - 1];
  const askFeedback = !!lastMessage && lastMessage.authorKind !== "customer" && t.status !== "resolved";
  return (
    <div className="page help-page help-ticket help-ticket-customer">
      <UnsavedGuard dirty={!!text.trim() || images.length > 0} />
      <PageHeader
        back={<BackToHelp />}
        title={t.title}
        subtitle={
          <span className="help-subtitle">
            <KindBadge kind={t.kind} />
            <span className={`badge ${ticketTone(t.status)}`}>{statusLabel[t.status]}</span>
            <span title={formatDateTime(t.createdAt)}>Sent {formatRelative(t.createdAt)}</span>
          </span>
        }
      />
      <ol className="help-thread" aria-label="Conversation">
        {detail.messages.map((m) => (
          <CustomerMessage key={m.id} orgId={orgId} ticketId={id} message={m} />
        ))}
      </ol>
      {askFeedback && (
        <div className="card help-feedback">
          <span>Did that reply help?</span>
          <div className="help-feedback-actions">
            <button type="button" className="btn" disabled={feedback.isPending} onClick={() => feedback.mutate(false)}>No, I still need help</button>
            <button type="button" className="btn primary" disabled={feedback.isPending} onClick={() => feedback.mutate(true)}>Yes, it helped</button>
          </div>
        </div>
      )}
      <form
        className="card help-reply"
        onSubmit={(e) => {
          e.preventDefault();
          if (text.trim() && !textIssue && !imageError && !reply.isPending) reply.mutate();
        }}
      >
        <Field label="Reply" error={textIssue}>
          {(p) => (
            <textarea
              {...p}
              className="input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onPaste={(e) => {
                const pasted = pastedImages(e);
                if (pasted.length === 0) return;
                e.preventDefault();
                const next = takeImages(images, pasted);
                setImages(next.files);
                setImageError(next.error);
              }}
              rows={5}
              placeholder="Add more detail or ask a follow-up…"
            />
          )}
        </Field>
        <ImagePicker files={images} error={imageError} onChange={(next, err) => { setImages(next); setImageError(err); }}>
          <BusyButton className="btn primary" type="submit" busy={reply.isPending} busyLabel="Sending…" disabled={!text.trim() || !!textIssue || !!imageError}>Send reply</BusyButton>
        </ImagePicker>
      </form>
    </div>
  );
}

function CustomerMessage({ orgId, ticketId, message: m }: { orgId: string; ticketId: string; message: HelpMessage }) {
  const mine = m.authorKind === "customer";
  return (
    <ThreadMessage
      name={mine ? "You" : m.authorKind === "staff" ? m.authorEmail || "Backstory" : "Backstory"}
      role={mine ? m.authorEmail || "" : "Backstory support"}
      tone={mine ? "customer" : m.authorKind === "staff" ? "staff" : "ai"}
      at={m.createdAt}
      citations={m.citations}
    >
      <p>{m.body}</p>
      <MessageImages paths={(m.attachments ?? []).map((a) => `/v1/orgs/${encodeURIComponent(orgId)}/help/tickets/${encodeURIComponent(ticketId)}/attachments/${encodeURIComponent(a.id)}`)} />
    </ThreadMessage>
  );
}

export function HelpNavLink() {
  const navigate = useNavigate();
  return (
    <button type="button" className="btn ghost" onClick={() => void navigate({ to: "/help" })}>
      Help
    </button>
  );
}
